import type { TFunction } from 'i18next'
import type { Translate } from 'src/components/BirdieBoard/tileViewModel'
import type { BirdieVenue, RoundOdds } from 'src/types/domain/RoundOdds'
import { safeLocale } from 'src/utils/safeLocale'
import { birdieBoardCopy, birdieGolferCardCopy, feetFromMetres } from './birdieCopy'

/**
 * The decimal scores' scale — the same five the pills are drawn on
 * (`CAPSULE_PILL_COUNT`). Kept as a local literal rather than imported from the
 * board so this model stays free of component-layer dependencies; the two are
 * pinned to each other by `birdieCardModel.test.ts`.
 */
const SCORE_SCALE = 5

/** A golfer's handicap runs 0 (best) to this (worst) in the engine's cards. */
const MAX_HANDICAP = 36

const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, value))

/**
 * The golfer's level with a decimal — their TIER plus where they sit inside it
 * (B16b, signed off 2026-09-08):
 *
 *     levelScore = levelStars + clamp((36 − handicap) / 36, 0, 0.99)
 *
 * so two "club" golfers no longer read as the same 3, which is the whole point
 * of the decimal: a lower handicap is a better golfer, so it fills more of the
 * tier's own pill segment.
 *
 * PRESENTATION ONLY. Nothing here reaches a price: every multiplier on the
 * board comes from the engine's committed paytable via `birdieMarkets.ts`, and
 * the level tier itself is what the engine hashes. This number exists so the
 * capsule's fourth pill can be part-filled.
 *
 * An unknown handicap yields the INTEGER part alone — the tier is still true,
 * and inventing a position inside it from nothing would be a number the player
 * could not act on. The result is capped at the scale it is drawn on: the
 * readout says "n/5", so a `pro` (5 stars) with a scratch handicap has to read
 * 5.0/5 rather than 5.99/5.
 */
export function levelScoreFor(levelStars: number, handicap: number | undefined): number {
  if (!Number.isFinite(levelStars) || levelStars <= 0) return 0
  if (handicap === undefined || !Number.isFinite(handicap)) {
    return Math.min(levelStars, SCORE_SCALE)
  }
  const within = clamp((MAX_HANDICAP - handicap) / MAX_HANDICAP, 0, 0.99)
  return Math.min(levelStars + within, SCORE_SCALE)
}

/**
 * How hard this green plays, with a decimal (B16b):
 *
 *     greenScore = difficulty + clamp((distanceM − 1) / 10, 0, 0.9)
 *
 * The engine's `difficulty` is the green's own 1..5 tier; the putt LENGTH is
 * what separates two rounds on the same green, and a longer putt is a harder
 * one. Same rules as `levelScoreFor`: presentation only, integer part alone
 * when the round carries no distance, capped at the scale it is drawn on.
 */
export function greenScoreFor(difficulty: number, distanceM: number | undefined): number {
  if (!Number.isFinite(difficulty) || difficulty <= 0) return 0
  if (distanceM === undefined || !Number.isFinite(distanceM)) {
    return Math.min(difficulty, SCORE_SCALE)
  }
  const within = clamp((distanceM - 1) / 10, 0, 0.9)
  return Math.min(difficulty + within, SCORE_SCALE)
}

export const LEVEL_STARS: Record<string, number> = {
  beginner: 1,
  amateur: 2,
  club: 3,
  semi_pro: 4,
  pro: 5
}

export interface BirdieCardView {
  makeRate: string
  /**
   * The number `makeRate` renders, as a percentage (B12: the capsule counts up
   * to it). It has to travel as a number — a formatted string cannot be
   * interpolated, and in `ar` its digits are not even ASCII.
   */
  makeRatePercent: number
  /**
   * `makeRate` at an arbitrary percentage, so every intermediate count-up frame
   * goes through the round's OWN formatter (B12). A second `Intl` call inside
   * the component would be a second place to get `safeLocale` wrong, and
   * `en-soc` takes the whole card down when that happens.
   */
  makeRateAt: (percent: number) => string
  makeRateCaption: string
  golferName: string
  golferLine: string
  /**
   * The golfer's age on its own, localised — the capsule's figures line shows
   * it beside a person icon rather than inside `golferLine`'s "·" list (B16b:
   * "as few labels as possible … icons and other visuals").
   */
  ageLabel: string
  /** "HCP 12", or absent when the round's card carries no handicap. */
  handicapLabel?: string
  levelStars: number
  difficultyStars: number
  /**
   * `levelStars` / `difficultyStars` with the decimal the capsule's pills draw
   * (`levelScoreFor` / `greenScoreFor`). Numbers, not strings, because they are
   * counted up — the same reason `makeRatePercent` is a number.
   */
  levelScore: number
  greenScore: number
  /**
   * A score at an arbitrary value, one decimal, through the round's OWN
   * formatter — so every count-up frame is localised (`ar` does not use ASCII
   * digits, `de` does not use a full stop) and there is only one place
   * `safeLocale` can be got wrong. Mirrors `makeRateAt`.
   */
  scoreAt: (value: number) => string
  /**
   * The plaque's own accessible name (B11 item 9 / review 21): the `role="group"`
   * shipped nameless, so a screen reader announced an unlabelled group of
   * numbers next to the stream.
   */
  title: string
  /**
   * The two star rows' labels — visible AND in their `aria-label`. B6 encoded
   * "which row is which" in colour alone (gold = level, green = difficulty),
   * which is invisible to a screen reader and to a colour-blind player
   * (review 14).
   */
  levelLabel: string
  difficultyLabel: string
  tempo: RoundOdds['card']['tempo']
  tempoLabel: string
  headshotUrl?: string
  /**
   * The putt distance on its own ("7 m"), for the capsule's identity line —
   * which shows it in EVERY state (B16, owner: "always show the golfer's name,
   * avatar, and the length, e.g. 3 m, 6 m"). `location.venueLine` already
   * carries it, but glued to the hole number ("Hole 4 · 7 m"), and the capsule
   * has room for the distance only.
   *
   * Known from `race_ready` onwards: the engine publishes the length with the
   * card (E2), so it is there while betting is open. The venue on
   * `race_started` still wins when it lands — normally the same number, and
   * the revealed green is the authority on the putt actually being played.
   * Undefined only when neither carries one; the identity line then renders
   * the name alone rather than a placeholder.
   */
  distanceLabel?: string
  location:
    | { state: 'pending'; label: string }
    | { state: 'revealed'; label: string; venueLine?: string }
}

/**
 * Owner decision 2026-09-05: transparent AND branded — the headline is the REAL
 * make rate per putt (card.makeRatePpm), never a derived score; level and green
 * difficulty are stars. Every number goes through safeLocale(): `en-soc` is a
 * real language with an invalid BCP 47 tag and every Intl constructor throws on
 * it, which would take the whole card down with no RUM trace.
 *
 * Every sentence comes from `birdieCopy.ts` — the ONE place the extractor sees
 * these literals, already translated in 16 locales. Never retype a literal here:
 * `i18n:cleanup` would delete the translation the moment the two copies drift.
 */
export function buildBirdieCardView(
  odds: RoundOdds,
  started: { location?: string; venue?: BirdieVenue } | undefined,
  language: string,
  t: Translate
): BirdieCardView {
  const locale = safeLocale(language)
  const percent = new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0 })
  const integer = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 })
  const { golfer, difficulty, makeRatePpm, tempo } = odds.card
  const num = (value: number | undefined): string =>
    value === undefined ? '' : integer.format(value)

  // The putt length, in the order of authority: the revealed green first, the
  // committed card second (B16/E2 — this is what lets the capsule show "3 m"
  // while betting is still open). Normally the same number; when they differ,
  // the clip being played is the one to believe. The venue LINE below stays
  // strictly venue-driven — it describes the green that was revealed, so it
  // must not borrow a number the reveal did not carry.
  const distanceM = started?.venue?.distance_m ?? odds.card.distanceM

  // `birdieCopy` is typed against i18next's branded TFunction; the card only
  // ever needs the `(key, options) => string` shape the exported `Translate`
  // alias documents — the same cast `tileViewModel.ts` makes for
  // `getTranslatedMarbleColor`.
  const translate = t as unknown as TFunction
  const board = birdieBoardCopy(translate)
  const copy = birdieGolferCardCopy(translate, {
    // A plus handicap (better than scratch) is carried negative and READS "+1", as golfers write it —
    // "HCP -1" would be a different number to anyone who knows the game (owner's boss, 2026-09-22).
    handicap:
      golfer.handicap !== undefined && golfer.handicap < 0
        ? `+${num(-golfer.handicap)}`
        : num(golfer.handicap),
    hole: num(started?.venue?.hole),
    distance: num(distanceM === undefined ? undefined : feetFromMetres(distanceM))
  })

  const ageLabel = integer.format(golfer.age)
  const golferParts = [ageLabel]
  if (golfer.handicap !== undefined) golferParts.push(copy.handicap)

  // One decimal, always — "4" beside "3.2" reads as a different kind of
  // number. `minimumFractionDigits` is what pins the trailing zero on a whole
  // score, and both bounds go through the same `safeLocale`d formatter.
  const score = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  })
  const scoreAt = (value: number): string => score.format(value)
  const levelStars = LEVEL_STARS[golfer.level] ?? 0

  // A lookup rather than a chained ternary: `no-nested-ternary` is an error
  // here, and the record makes a new tempo a compile error instead of a silent
  // fall-through to "Balanced".
  const tempoLabels: Record<RoundOdds['card']['tempo'], string> = {
    favourite: copy.favourite,
    balanced: copy.balanced,
    longshot: copy.longshot
  }

  let location: BirdieCardView['location'] = {
    state: 'pending',
    label: board.greenHiddenUntilClip
  }
  if (started?.location) {
    const venueBits: string[] = []
    if (started.venue?.hole !== undefined) venueBits.push(copy.hole)
    if (started.venue?.distance_m !== undefined) venueBits.push(copy.distance)
    location = {
      state: 'revealed',
      label: started.location,
      ...(venueBits.length > 0 ? { venueLine: venueBits.join(' · ') } : {})
    }
  }

  // One formatter, three call sites: the resting string, the count-up target
  // and every frame in between (B12).
  const makeRateAt = (value: number): string => percent.format(value / 100)

  return {
    makeRate: percent.format(makeRatePpm / 1_000_000),
    makeRatePercent: Math.round((makeRatePpm / 1_000_000) * 100),
    makeRateAt,
    makeRateCaption: board.makeChanceCaption,
    golferName: golfer.name,
    golferLine: golferParts.join(' · '),
    ageLabel,
    ...(golfer.handicap !== undefined ? { handicapLabel: copy.handicap } : {}),
    levelStars,
    difficultyStars: difficulty,
    levelScore: levelScoreFor(levelStars, golfer.handicap),
    greenScore: greenScoreFor(difficulty, distanceM),
    scoreAt,
    title: copy.title,
    levelLabel: copy.level,
    difficultyLabel: copy.greenDifficulty,
    tempo,
    tempoLabel: tempoLabels[tempo],
    ...(golfer.headshotUrl !== undefined ? { headshotUrl: golfer.headshotUrl } : {}),
    ...(distanceM !== undefined ? { distanceLabel: copy.distance } : {}),
    location
  }
}
